import { AfterViewInit, Component, ElementRef, Inject, Input, OnDestroy, ViewChild } from "@angular/core";

import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Object3DUserData } from "~/src/utils/shape";
import { StabilityService } from "../../services/stability/stability.service";

interface PhysicsObject {
    mesh: THREE.Mesh;
    body: RAPIER.RigidBody;
    localOffset: THREE.Vector3; // offset from body origin
    localRotation: THREE.Quaternion;
}

@Component({
    selector: "app-preview-stability",
    templateUrl: "./preview-stability.component.html",
    styleUrl: "./preview-stability.component.scss",
    standalone: false,
})
export class PreviewStabilityComponent implements AfterViewInit, OnDestroy {
    @ViewChild("container", { static: true })
    container!: ElementRef<HTMLDivElement>;

    @Input() unstableStructures!: THREE.Mesh[][];

    private scene!: THREE.Scene;
    private camera!: THREE.PerspectiveCamera;
    private renderer!: THREE.WebGLRenderer;

    private controls!: OrbitControls;

    private world!: RAPIER.World;

    private physicsObjects: PhysicsObject[] = [];

    private animationId = 0;

    constructor(private stabilityService: StabilityService) {}

    async ngAfterViewInit(): Promise<void> {
        const el = this.container.nativeElement;

        await this.initializeRapier();

        this.initializeScene();

        this.createLights();

        this.createGround();

        this.createPreviewMeshes();

        this.animate();
    }

    private async initializeRapier(): Promise<void> {
        await RAPIER.init();
        const gravity = new RAPIER.Vector3(0.0, -9.81, 0.0);
        this.world = new RAPIER.World(gravity);
    }

    private initializeScene(): void {
        const width = this.container.nativeElement.clientWidth;
        const height = this.container.nativeElement.clientHeight;

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0xf4f4f4);

        this.camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 10000);
        this.camera.position.set(600, 700, 500);
        this.camera.lookAt(0, 0, 0);

        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
        });

        this.renderer.setSize(width, height);

        this.container.nativeElement.appendChild(this.renderer.domElement);

        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
    }

    private createLights(): void {
        const ambient = new THREE.AmbientLight(0xffffff, 1);

        const directional = new THREE.DirectionalLight(0xffffff, 2);

        directional.position.set(100, 200, 100);

        this.scene.add(ambient, directional);
    }

    private createGround(): void {
        const groundMesh = new THREE.Mesh(
            new THREE.BoxGeometry(1000, 1, 1000),
            new THREE.MeshStandardMaterial({
                color: 0x808080,
                roughness: 1,
                metalness: 0,
            }),
        );

        groundMesh.position.y = -1;

        this.scene.add(groundMesh);

        const groundBody = this.world.createRigidBody(RAPIER.RigidBodyDesc.fixed());

        this.world.createCollider(RAPIER.ColliderDesc.cuboid(500, 1, 500), groundBody);
    }

    private createPreviewMeshes(): void {
        for (const structure of this.unstableStructures) {
            // Compute center of mass of the whole structure
            const center = new THREE.Vector3();
            for (const mesh of structure) {
                center.add(mesh.position);
            }
            center.divideScalar(structure.length);

            // Create ONE body at the structure's center
            const structureBody = this.world.createRigidBody(
                RAPIER.RigidBodyDesc.dynamic().setTranslation(center.x, center.y, center.z),
            );

            for (const original of structure) {
                const originalColors = original.userData[Object3DUserData.originalColors];
                const clone = original.clone();
                clone.material = Array.isArray(original.material)
                    ? original.material.map((m) => m.clone())
                    : original.material.clone();
                if (originalColors && Array.isArray(originalColors)) {
                    (clone.material as THREE.MeshBasicMaterial[]).forEach((material, index) => {
                        material.color.set(originalColors[index]);
                        material.transparent = false;
                        material.opacity = 1;
                    });
                }
                this.scene.add(clone);

                const box = new THREE.Box3().setFromObject(clone);
                const size = new THREE.Vector3();
                box.getSize(size);

                // Offset = mesh position relative to structure center
                const localOffset = clone.position.clone().sub(center);

                const localRotation = clone.quaternion.clone();

                // Attach collider to body at local offset
                this.world.createCollider(
                    RAPIER.ColliderDesc.cuboid(size.x / 2, size.y / 2, size.z / 2)
                        .setTranslation(localOffset.x, localOffset.y, localOffset.z)
                        .setRotation({
                            x: localRotation.x,
                            y: localRotation.y,
                            z: localRotation.z,
                            w: localRotation.w,
                        }),
                    structureBody,
                );

                this.physicsObjects.push({
                    mesh: clone,
                    body: structureBody,
                    localOffset,
                    localRotation,
                });
            }

            // Apply impulse on the heavy/offset side to trigger tipping
            const heavySide = this.computeHeavySideImpulse(structure, center);
            structureBody.applyImpulse(heavySide, true);
        }
    }

    private computeHeavySideImpulse(
        structure: THREE.Mesh[],
        center: THREE.Vector3,
    ): { x: number; y: number; z: number } {
        // Find which side has more mass (more meshes offset from center)
        const offset = new THREE.Vector3();
        for (const mesh of structure) {
            offset.add(mesh.position.clone().sub(center));
        }

        // Normalize and scale — push toward the heavy side
        offset.normalize().multiplyScalar(200);

        return { x: offset.x, y: 0, z: offset.z };
    }

    private animate = (): void => {
        this.animationId = requestAnimationFrame(this.animate);
        this.controls.update();

        // Step multiple times per frame
        for (let i = 0; i < 7; i++) {
            this.world.step();
        }

        for (const obj of this.physicsObjects) {
            const bodyPos = obj.body.translation();
            const bodyRot = obj.body.rotation();

            const bodyQuat = new THREE.Quaternion(bodyRot.x, bodyRot.y, bodyRot.z, bodyRot.w);

            obj.mesh.position.copy(
                obj.localOffset
                    .clone()
                    .applyQuaternion(bodyQuat)
                    .add(new THREE.Vector3(bodyPos.x, bodyPos.y, bodyPos.z)),
            );

            obj.mesh.quaternion.copy(bodyQuat.clone().multiply(obj.localRotation));
        }

        this.renderer.render(this.scene, this.camera);
    };

    ngOnDestroy(): void {
        cancelAnimationFrame(this.animationId);

        this.renderer.dispose();

        this.scene.clear();

        this.world.free();

        this.controls.dispose();
    }

    private getCenterOfMass(meshes: THREE.Mesh[]): THREE.Vector3 {
        const center = new THREE.Vector3();

        for (const mesh of meshes) {
            center.add(mesh.position);
        }

        return center.divideScalar(meshes.length);
    }

    private getSupportBounds(groundMeshes: THREE.Mesh[]) {
        let minX = Infinity;
        let maxX = -Infinity;

        let minZ = Infinity;
        let maxZ = -Infinity;

        const HALF_GRID_SIZE = 12.5;

        for (const mesh of groundMeshes) {
            minX = Math.min(minX, mesh.position.x - HALF_GRID_SIZE);
            maxX = Math.max(maxX, mesh.position.x + HALF_GRID_SIZE);

            minZ = Math.min(minZ, mesh.position.z - HALF_GRID_SIZE);
            maxZ = Math.max(maxZ, mesh.position.z + HALF_GRID_SIZE);
        }

        return {
            minX,
            maxX,
            minZ,
            maxZ,
        };
    }
}
